import { createClient, Client } from 'graphql-ws';
import { ApolloClient } from '@apollo/client';
import { ON_CASE_UPDATED } from '../queries/CaseUpdatedSubscription';
import { loggerService } from '@brainpool/logger';
import { ConnectionStatus, WebSocketMessage } from '../types/WebSocket';
import {
  updateCaseConnectionStatus,
  handleDocumentUpdated,
  handleCaseUpdated,
} from './websocketHelperFunctions';

interface WebSocketConnection {
  client: Client;
  caseId: string;
  status: 'connected' | 'disconnected' | 'reconnecting';
  lastMessageTime: number;
  reconnectAttempts: number;
}

interface ChatMessageChunk {
  id: string;
  _sk: string;
  data_b64: string;
  role: string;
  created_date: string;
  created_by: string;
  next_chunk: string;
}

const connectionStatusMap = new Map<string, 'connected' | 'disconnected' | 'reconnecting'>();

export class WebsocketService {
  private static instance: WebsocketService | null = null;
  private client: Client | null = null;
  private connections: Map<string, WebSocketConnection> = new Map();
  private messageChunkMap: Map<string, ChatMessageChunk[]> = new Map();
  private connectionListeners: Map<string, Set<(status: ConnectionStatus) => void>> = new Map();
  private apolloClient: ApolloClient | null = null;

  private constructor() {
    this.connections = new Map();
    this.messageChunkMap = new Map();
    this.connectionListeners = new Map();
    this.client = null;
  }

  public static getInstance(): WebsocketService {
    if (!WebsocketService.instance) {
      WebsocketService.instance = new WebsocketService();
    }
    return WebsocketService.instance;
  }

  public setApolloClient(client: ApolloClient): void {
    this.apolloClient = client;
    loggerService.log({
      message: 'Apollo client set in WebSocket service',
      logLevel: 'info',
      context: { action: 'setApolloClient' },
    });
  }

  public createWebSocketClient(websocketUrl: string, token: string): void {
    this.disposeExistingClient();
    this.initializeNewClient(websocketUrl, token);

    loggerService.log({
      message: 'WebSocket client created successfully',
      logLevel: 'info',
      context: { action: 'createWebSocketClient', websocketUrl },
    });
  }

  private disposeExistingClient(): void {
    if (this.client) {
      try {
        this.client.dispose();
        this.connections.clear();
        this.messageChunkMap.clear();
      } catch (error) {
        loggerService.log({
          message: 'Error disposing existing WebSocket client',
          logLevel: 'error',
          error: error as Error,
          context: { action: 'disposeExistingClient' },
        });
      }
    }
  }

  private initializeNewClient(websocketUrl: string, token: string): void {
    setTimeout(() => {
      try {
        this.client = createClient({
          url: websocketUrl,
          connectionParams: {
            Authorization: `Bearer ${token}`,
          },
          retryAttempts: 5,
          retryWait: async retries => {
            return new Promise(resolve => setTimeout(resolve, Math.min(retries * 1000, 10000)));
          },
          lazy: false,
        });
      } catch (error) {
        loggerService.log({
          message: 'Error creating WebSocket client',
          logLevel: 'error',
          error: error as Error,
          context: { action: 'initializeNewClient' },
        });
        this.client = null;
      }
    }, 100);
  }

  public addConnectionListener(
    caseId: string,
    listener: (status: ConnectionStatus) => void
  ): () => void {
    if (!this.connectionListeners.has(caseId)) {
      this.connectionListeners.set(caseId, new Set());
    }

    const listeners = this.connectionListeners.get(caseId);
    listeners?.add(listener);

    return () => {
      const listeners = this.connectionListeners.get(caseId);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.connectionListeners.delete(caseId);
        }
      }
    };
  }

  public getConnectionStatus(caseId: string): ConnectionStatus {
    return connectionStatusMap.get(caseId) || 'disconnected';
  }

  private updateConnectionStatus(caseId: string, status: ConnectionStatus): void {
    const currentStatus = connectionStatusMap.get(caseId);

    if (currentStatus !== status) {
      connectionStatusMap.set(caseId, status);

      const listeners = this.connectionListeners.get(caseId);
      if (listeners) {
        listeners.forEach(listener => {
          try {
            listener(status);
          } catch (error) {
            loggerService.log({
              message: `Error in connection listener for case ${caseId}`,
              logLevel: 'error',
              error: error as Error,
              context: { action: 'updateConnectionStatus', caseId },
            });
          }
        });
      }
    }
  }

  public subscribeToCaseUpdates(caseId: string): void {
    if (!this.validateSubscription(caseId)) return;

    try {
      loggerService.log({
        message: `Subscribing to case updates: ${caseId}`,
        logLevel: 'info',
        context: { action: 'subscribeToCaseUpdates', caseId },
      });

      this.initializeConnection(caseId);
      this.setupSubscription(caseId);
      updateCaseConnectionStatus(caseId, 'connected');
    } catch (error) {
      this.handleSubscriptionError(caseId, error);
    }
  }

  private validateSubscription(caseId: string): boolean {
    if (!this.client) {
      loggerService.log({
        message: 'WebSocket client not initialized',
        logLevel: 'error',
        context: { action: 'validateSubscription', caseId },
      });
      return false;
    }

    if (!this.apolloClient) {
      loggerService.log({
        message: 'Apollo client not initialized in WebSocket service',
        logLevel: 'error',
        context: { action: 'validateSubscription', caseId },
      });
      return false;
    }

    if (!this.apolloClient) {
      loggerService.log({
        message: 'Apollo client not initialized in WebSocket service',
        logLevel: 'error',
        context: {
          action: 'validateSubscription',
          caseId,
        },
      });
      return false;
    }

    const existingStatus = connectionStatusMap.get(caseId);
    if (existingStatus === 'connected') {
      loggerService.log({
        message: `Already subscribed to case: ${caseId}`,
        logLevel: 'warning',
        context: { action: 'validateSubscription', caseId },
      });
      return false;
    }

    return true;
  }

  private initializeConnection(caseId: string): void {
    this.connections.set(caseId, {
      client: this.client!,
      caseId,
      status: 'connected',
      lastMessageTime: Date.now(),
      reconnectAttempts: 0,
    });
    this.updateConnectionStatus(caseId, 'connected');
  }

  private setupSubscription(caseId: string): void {
    this.client!.subscribe(
      {
        query: ON_CASE_UPDATED,
        variables: { case_id: caseId },
      },
      {
        next: (data: WebSocketMessage) => this.handleMessage(caseId, data),
        error: error => this.handleSubscriptionError(caseId, error),
        complete: () => this.handleSubscriptionComplete(caseId),
      }
    );

    loggerService.log({
      message: `WebSocket subscription established for case: ${caseId}`,
      logLevel: 'info',
      context: { action: 'setupSubscription', caseId },
    });
  }

  private handleMessage(caseId: string, message: WebSocketMessage): void {
    try {
      loggerService.log({
        message: `Received WebSocket message for case: ${caseId}`,
        logLevel: 'info',
        context: {
          action: 'handleMessage',
          caseId,
          message: JSON.stringify(message),
        },
      });

      const connection = this.connections.get(caseId);
      if (connection) {
        connection.lastMessageTime = Date.now();
      }

      const { type, data } = message.data.onCaseUpdated;

      this.processWebSocketMessage(caseId, type, data);
    } catch (error) {
      loggerService.log({
        message: `Error processing WebSocket message for case ${caseId}`,
        logLevel: 'error',
        error: error as Error,
        context: {
          action: 'handleMessage',
          caseId,
          message: JSON.stringify(message),
        },
      });
    }
  }

  private processWebSocketMessage(caseId: string, type: any, data: WebSocketMessage): void {
    switch (type) {
      case 'case.updated':
        handleCaseUpdated(caseId, data);
        break;
      case 'document.updated':
        handleDocumentUpdated(caseId, data);
        break;
      default:
        loggerService.log({
          message: `WebSocket message type: ${type}`,
          logLevel: 'info',
          context: { action: 'processWebSocketMessage', caseId, messageType: type },
        });
    }
  }

  private handleSubscriptionError(caseId: string, error: any): void {
    loggerService.log({
      message: 'WebSocket subscription error for case ' + caseId,
      logLevel: 'error',
      error: error as Error,
      context: { action: 'handleSubscriptionError', caseId },
      userMessage: {
        message:
          'We have encountered an error with connection to case ' + caseId + '. Please try again.',
      },
    });
    this.updateConnectionStatus(caseId, 'disconnected');
    updateCaseConnectionStatus(caseId, 'disconnected');
    this.connections.delete(caseId);
  }

  private handleSubscriptionComplete(caseId: string): void {
    if (this.connections.has(caseId)) {
      loggerService.log({
        message: `WebSocket subscription completed for case: ${caseId}`,
        logLevel: 'info',
        context: { action: 'handleSubscriptionComplete', caseId },
      });
      this.updateConnectionStatus(caseId, 'disconnected');
      updateCaseConnectionStatus(caseId, 'disconnected');
    }
  }

  public disconnect(caseId: string): void {
    try {
      const connection = this.connections.get(caseId);
      if (!connection) {
        return;
      }

      this.connections.delete(caseId);
      this.updateConnectionStatus(caseId, 'disconnected');
      updateCaseConnectionStatus(caseId, 'disconnected');

      loggerService.log({
        message: `Disconnected from case: ${caseId}`,
        logLevel: 'info',
        context: { action: 'disconnect', caseId },
      });
    } catch (error) {
      loggerService.log({
        message: `Error during disconnection for case ${caseId}`,
        logLevel: 'error',
        error: error as Error,
        context: { action: 'disconnect', caseId },
      });
    }
  }
}

export const wsService = WebsocketService.getInstance();

// Initialize WebSocket helpers with Apollo client
export function initializeWebSocketHelpers(client: ApolloClient) {
  const wsService = WebsocketService.getInstance();
  wsService.setApolloClient(client);

  loggerService.log({
    message: 'WebSocket helpers initialized',
    logLevel: 'info',
    context: { action: 'initializeWebSocketHelpers' },
  });
}
